import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ApiService } from './api.service';
import { GeospatialMapBridgeService } from './geospatial-map-bridge.service';
import { LrsRoute, LrsService } from './lrs.service';
import {
  BOQ_EXPLORER_TREE,
  BoqExplorerNode,
  defaultExplorerChecked,
  flattenExplorerNodes,
} from 'src/app/components/geospatial/gis-boq-workbench/gis-boq-explorer.model';

export interface GisBoqWorkbenchItem {
  category: string;
  item_code?: string;
  item_description: string;
  layer?: string | null;
  length_m?: number | null;
  width_m?: number | null;
  thickness_m?: number | null;
  quantity: number;
  volume?: number | null;
  unit: string;
  unit_rate: number;
  cost: number;
  /** MoRTH specification reference (informational), e.g. "MoRTH 406" */
  morth_ref?: string | null;
}

export interface GisBoqWorkbenchTotals {
  pavement: number;
  shoulder: number;
  structure: number;
  utility: number;
  safety: number;
  furniture: number;
  grand: number;
}

@Injectable({ providedIn: 'root' })
export class GisBoqWorkbenchService {
  readonly tree = BOQ_EXPLORER_TREE;
  routes: LrsRoute[] = [];
  routeId: number | null = null;
  projectId = '';
  startChainage = '0+000';
  endChainage = '1+000';
  laneWidthM = 7;
  shoulderWidthM = 2.5;
  median: 'concrete' | 'none' = 'concrete';
  checked: Record<string, boolean> = defaultExplorerChecked();
  expanded: Record<string, boolean> = {
    structures: true,
    furniture: true,
    safety: true,
    utilities: true,
  };

  previewItems: GisBoqWorkbenchItem[] = [];
  /** User-edited unit rates keyed by item_code; survive Recalculate. */
  rateOverrides: Record<string, number> = {};
  totals: GisBoqWorkbenchTotals = {
    pavement: 0,
    shoulder: 0,
    structure: 0,
    utility: 0,
    safety: 0,
    furniture: 0,
    grand: 0,
  };
  loading = false;
  applying = false;
  error = '';
  status = '';
  appliedRoadBoqId: number | null = null;
  lengthKm = 0;
  geometry: { type: string; coordinates: any } | null = null;
  rateCard: {
    pavement?: any[];
    assets?: Record<string, any>;
    shoulder?: any;
    median?: any;
  } | null = null;
  savingRates = false;

  private readonly previewTick = new Subject<void>();
  private readonly stateSubject = new BehaviorSubject<void>(undefined);
  readonly state$ = this.stateSubject.asObservable();
  private previewSub?: Subscription;
  private geometrySub?: Subscription;
  private started = false;

  constructor(
    private readonly api: ApiService,
    private readonly lrs: LrsService,
    private readonly router: Router,
    private readonly mapBridge: GeospatialMapBridgeService
  ) {}

  start(projectId: string): void {
    this.projectId = projectId || '';
    if (!this.previewSub) {
      this.previewSub = this.previewTick.pipe(debounceTime(400)).subscribe(() => this.runPreview());
    }
    this.started = true;
    this.loadRateCard();
    this.bindGeometry();
    this.loadRoutes();
    this.publishLayerFilter();
    this.notify();
    setTimeout(() => this.mapBridge.refreshMapSize(), 350);
  }

  stop(): void {
    this.started = false;
  }

  destroy(): void {
    this.previewSub?.unsubscribe();
    this.previewSub = undefined;
    this.geometrySub?.unsubscribe();
    this.geometrySub = undefined;
    this.started = false;
  }

  private notify(): void {
    this.stateSubject.next();
  }

  private bindGeometry(): void {
    if (this.geometrySub) return;
    this.geometrySub = this.mapBridge.boqGeometry$.subscribe((geom) => {
      this.geometry = geom;
      if (this.started) this.schedulePreview();
      this.notify();
    });
  }

  private loadRateCard(): void {
    this.api.getGisBoqRateCard().subscribe({
      next: (res: any) => {
        this.rateCard = res?.data || null;
        this.notify();
      },
      error: () => {
        this.rateCard = null;
      },
    });
  }

  saveRates(): void {
    if (this.savingRates) return;
    let pavement = (this.rateCard?.pavement || []).map((row: any) => {
      const override = row.item_code ? this.rateOverrides[row.item_code] : undefined;
      return {
        ...row,
        unit_rate: override != null ? override : row.unit_rate,
      };
    });
    if (!pavement.length) {
      pavement = this.previewItems
        .filter((i) => i.category === 'pavement')
        .map((i) => ({
          item_code: i.item_code,
          layer: i.layer,
          item_description: i.item_description,
          thickness_m: i.thickness_m,
          unit_rate: i.unit_rate,
          category: 'pavement',
        }));
    }
    this.previewItems.forEach((item) => {
      if (!item.item_code) return;
      const pav = pavement.find((p: any) => p.item_code === item.item_code);
      if (pav) pav.unit_rate = item.unit_rate;
    });
    const assets = { ...(this.rateCard?.assets || {}) };
    this.previewItems.forEach((item) => {
      if (!item.item_code || !item.layer || this.rateOverrides[item.item_code] == null) return;
      if (assets[item.layer]) {
        assets[item.layer] = { ...assets[item.layer], unit_rate: this.rateOverrides[item.item_code] };
      }
    });
    const shoulder = { ...(this.rateCard?.shoulder || {}) };
    const shEarthen = this.previewItems.find((i) => i.item_code === 'WB-SH-EAR' || i.item_code === 'WB-SH-GSB');
    if (shEarthen) shoulder.unit_rate = shEarthen.unit_rate;
    const shPaved = this.previewItems.find((i) => i.item_code === 'WB-SH-PAV');
    if (shPaved) shoulder.paved_unit_rate = shPaved.unit_rate;
    const median = { ...(this.rateCard?.median || {}) };
    const med = this.previewItems.find((i) => i.item_code === 'WB-MED');
    if (med) median.unit_rate = med.unit_rate;

    this.savingRates = true;
    this.notify();
    this.api.saveGisBoqRateCard({ pavement, assets, shoulder, median }).subscribe({
      next: (res: any) => {
        this.savingRates = false;
        this.rateCard = res?.data || this.rateCard;
        this.status = 'Rate card saved';
        this.notify();
        this.schedulePreview();
      },
      error: (err) => {
        this.savingRates = false;
        this.error = err?.error?.error || 'Failed to save rate card';
        this.notify();
      },
    });
  }

  private loadRoutes(): void {
    const params = this.projectId ? { project_id: this.projectId } : undefined;
    this.lrs.listRoutes(params).subscribe({
      next: (res) => {
        this.routes = res.data || [];
        if (!this.routes.length && this.projectId) {
          this.lrs.listRoutes().subscribe({
            next: (all) => this.pickRoute(all.data || []),
            error: () => this.schedulePreview(),
          });
          return;
        }
        this.pickRoute(this.routes);
      },
      error: () => {
        this.error = 'Could not load LRS routes';
        this.notify();
      },
    });
  }

  private pickRoute(routes: LrsRoute[]): void {
    this.routes = routes;
    const demo = routes.find((r) => r.route_code === 'NH-DEMO-48');
    const next = demo || routes[0] || null;
    if (next && next.id !== this.routeId) {
      this.routeId = next.id;
      this.mapBridge.selectBoqRoute(next.id);
    } else if (next) {
      this.routeId = next.id;
    }
    this.schedulePreview();
    this.notify();
  }

  setRoute(id: number | null): void {
    this.routeId = id;
    if (id) this.mapBridge.selectBoqRoute(id);
    this.schedulePreview();
    this.notify();
  }

  setChainage(start: string, end: string): void {
    this.startChainage = start;
    this.endChainage = end;
    this.schedulePreview();
    this.notify();
  }

  setWidths(lane: number, shoulder: number): void {
    this.laneWidthM = lane;
    this.shoulderWidthM = shoulder;
    this.schedulePreview();
    this.notify();
  }

  setMedian(median: 'concrete' | 'none'): void {
    this.median = median;
    this.schedulePreview();
    this.notify();
  }

  toggleNode(id: string, on: boolean): void {
    this.checked[id] = on;
    const node = flattenExplorerNodes().find((n) => n.id === id);
    node?.children?.forEach((c) => this.toggleNode(c.id, on));
    this.publishLayerFilter();
    this.schedulePreview();
    this.notify();
  }

  toggleExpanded(id: string): void {
    this.expanded[id] = !this.expanded[id];
    this.notify();
  }

  recalculate(): void {
    this.runPreview();
  }

  updateRate(item: GisBoqWorkbenchItem, rate: number): void {
    const n = Number(rate);
    if (!Number.isFinite(n) || n < 0) return;
    const qty = Number(item.quantity) || 0;
    item.unit_rate = Math.round(n * 100) / 100;
    item.cost = Math.round(qty * item.unit_rate * 100) / 100;
    if (item.item_code) this.rateOverrides[item.item_code] = item.unit_rate;
    this.recomputeTotals();
    this.notify();
  }

  updateCost(item: GisBoqWorkbenchItem, cost: number): void {
    const n = Number(cost);
    if (!Number.isFinite(n) || n < 0) return;
    const qty = Number(item.quantity) || 0;
    item.cost = Math.round(n * 100) / 100;
    item.unit_rate = qty > 0 ? Math.round((item.cost / qty) * 100) / 100 : item.unit_rate;
    if (item.item_code) this.rateOverrides[item.item_code] = item.unit_rate;
    this.recomputeTotals();
    this.notify();
  }

  private applyRateOverrides(): void {
    this.previewItems.forEach((item) => {
      const code = item.item_code;
      if (!code || this.rateOverrides[code] == null) return;
      item.unit_rate = this.rateOverrides[code];
      item.cost = Math.round((Number(item.quantity) || 0) * item.unit_rate * 100) / 100;
    });
    this.recomputeTotals();
  }

  private recomputeTotals(): void {
    const totals: GisBoqWorkbenchTotals = {
      pavement: 0,
      shoulder: 0,
      structure: 0,
      utility: 0,
      safety: 0,
      furniture: 0,
      grand: 0,
    };
    const buckets: (keyof GisBoqWorkbenchTotals)[] = [
      'pavement',
      'shoulder',
      'structure',
      'utility',
      'safety',
      'furniture',
    ];
    this.previewItems.forEach((it) => {
      const cost = Number(it.cost) || 0;
      const cat = it.category as keyof GisBoqWorkbenchTotals;
      if (buckets.includes(cat)) {
        totals[cat] += cost;
      }
      totals.grand += cost;
    });
    buckets.forEach((k) => {
      totals[k] = Math.round(totals[k] * 100) / 100;
    });
    totals.grand = Math.round(totals.grand * 100) / 100;
    this.totals = totals;
  }

  schedulePreview(): void {
    if (!this.started) return;
    this.previewTick.next();
  }

  parseChainageToKm(value: string): number {
    const t = (value || '').trim();
    const plus = t.match(/^(-?\d+)\+(\d{1,3})$/);
    if (plus) return parseInt(plus[1], 10) + parseInt(plus[2], 10) / 1000;
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : 0;
  }

  enabledCategories(): string[] {
    const cats = new Set<string>();
    flattenExplorerNodes().forEach((n) => {
      if (n.category && this.checked[n.id] !== false) cats.add(n.category);
    });
    return Array.from(cats);
  }

  enabledTypeIds(): string[] {
    const ids = new Set<string>();
    const walk = (nodes: BoqExplorerNode[], parentOn: boolean) => {
      nodes.forEach((n) => {
        const on = parentOn && this.checked[n.id] !== false;
        if (on && n.typeIds) n.typeIds.forEach((t) => ids.add(t));
        if (n.children) walk(n.children, on);
      });
    };
    walk(this.tree, true);
    return Array.from(ids);
  }

  enabledCatalogIds(): string[] {
    const ids: string[] = ['ref-chainage'];
    const walk = (nodes: BoqExplorerNode[], parentOn: boolean) => {
      nodes.forEach((n) => {
        const on = parentOn && this.checked[n.id] !== false;
        if (on && n.catalogIds) ids.push(...n.catalogIds);
        if (n.children) walk(n.children, on);
      });
    };
    walk(this.tree, true);
    return ids;
  }

  private publishLayerFilter(): void {
    this.mapBridge.setBoqLayerFilter({
      typeIds: this.enabledTypeIds(),
      catalogIds: this.enabledCatalogIds(),
      alignmentOn: this.checked['alignment'] !== false,
    });
  }

  private previewPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      mode: 'workbench',
      target: 'road',
      domain: 'road',
      route_id: this.routeId,
      project_id: this.projectId || undefined,
      from_km: this.parseChainageToKm(this.startChainage),
      to_km: this.parseChainageToKm(this.endChainage),
      lane_width_m: this.laneWidthM,
      shoulder_width_m: this.shoulderWidthM,
      median: this.median,
      include_categories: this.enabledCategories(),
      include_types: this.enabledTypeIds(),
    };
    if (this.geometry) payload['geometry'] = this.geometry;
    return payload;
  }

  private runPreview(): void {
    if (!this.started) return;
    this.loading = true;
    this.error = '';
    this.notify();
    this.api.previewGisBoq(this.previewPayload()).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        this.previewItems = data.items || [];
        this.applyRateOverrides();
        this.lengthKm = data.length?.length_km || 0;
        this.loading = false;
        this.status = `${this.previewItems.length} items · ${this.lengthKm} km`;
        this.notify();
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.error || 'Preview failed';
        this.notify();
      },
    });
  }

  apply(): void {
    const route = this.routes.find((r) => r.id === this.routeId);
    const code = `RBQ-GIS-${Date.now().toString().slice(-6)}`;
    const name = `Road BoQ ${route?.route_code || 'GIS'} ${this.startChainage}–${this.endChainage}`;
    this.applying = true;
    this.error = '';
    this.notify();
    this.api
      .applyGisBoq({
        ...this.previewPayload(),
        boq_code: code,
        boq_name: name,
        administrative_authority: 'NHAI',
        road_type: 'Highway',
        road_boq_id: this.appliedRoadBoqId || undefined,
        item_overrides: this.previewItems
          .filter((i) => i.item_code)
          .map((i) => ({ item_code: i.item_code, unit_rate: i.unit_rate })),
      })
      .subscribe({
        next: (res: any) => {
          this.applying = false;
          const data = res?.data || res;
          this.appliedRoadBoqId = data.road_boq_id || this.appliedRoadBoqId;
          this.status = this.appliedRoadBoqId
            ? `Saved Road BoQ #${this.appliedRoadBoqId}`
            : 'Saved';
          this.notify();
        },
        error: (err) => {
          this.applying = false;
          this.error = err?.error?.error || 'Apply failed';
          this.notify();
        },
      });
  }

  openRoadBoq(): void {
    if (this.appliedRoadBoqId) {
      void this.router.navigate(['/home/road-boq', this.appliedRoadBoqId, 'edit']);
      return;
    }
    void this.router.navigate(['/home/road-boq-list']);
  }
}
